import React, { useRef } from "react";
import styled from "styled-components";
import {
  cubicBezier,
  motion,
  MotionValue,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import { ease } from "../tokens";
import { Inner, Reveal, STATIC_MODE } from "../primitives";
import TradingCard from "../TradingCard";
import { PALO_SECTION, CardData } from "../data";
import EraHeader from "./EraHeader";
import useMedia from "../useMedia";
import firstRescueDemo from "../../images/firstRescueDemo.webp";

const Wrap = styled.div`
  padding-bottom: clamp(120px, 22vh, 240px);
`;

/* ------------------------------------------------ pinned fan (desktop) */

const FanTrack = styled.div`
  position: relative;
  height: 280vh;
`;

const FanViewport = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const FanStage = styled.div`
  position: relative;
  width: 100%;
  height: 540px;
`;

const CardSlot = styled(motion.div)`
  position: absolute;
  left: 50%;
  top: 0;
  width: clamp(196px, 16.6vw, 240px);
  margin-left: calc(clamp(196px, 16.6vw, 240px) / -2);
  will-change: transform;
`;

/* Pile → fan: initial rotation/offset per card, straightening to a row. */
const START_ROTATE = [-8, -4, 0, 4, 8];
const START_Y = [8, -6, 0, 10, -4];

const cardMedia = (card: CardData) =>
  card.project.id === "firstrescue" ? (
    <img src={firstRescueDemo} alt="First Rescue app demo" />
  ) : undefined;

const FanCard: React.FC<{
  card: CardData;
  index: number;
  progress: MotionValue<number>;
}> = ({ card, index, progress }) => {
  const slot = (index - 2) * 106; // percent of card width
  const range: [number, number] = [0.08, 0.62];
  const x = useTransform(progress, range, ["0%", `${slot}%`], {
    ease: cubicBezier(...ease.out),
  });
  const rotate = useTransform(progress, range, [START_ROTATE[index], 0]);
  const y = useTransform(progress, range, [START_Y[index], 0]);

  if (STATIC_MODE) {
    return (
      <CardSlot
        as="div"
        style={{ transform: `translateX(${slot}%)`, zIndex: 2 }}
      >
        <TradingCard card={card} media={cardMedia(card)} />
      </CardSlot>
    );
  }

  return (
    <CardSlot style={{ x, rotate, y, zIndex: index === 2 ? 3 : 2 }}>
      <TradingCard card={card} media={cardMedia(card)} />
    </CardSlot>
  );
};

/* --------------------------------------------- static column (mobile) */

const MobileColumn = styled.div`
  margin-top: clamp(32px, 6vh, 64px);
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 480px;
  margin-left: auto;
  margin-right: auto;
`;

/**
 * Section — 02 Palo IT (2020–2023): five cards fan out and straighten
 * while the viewport is pinned (transforms only). Mobile degrades to a
 * simple stagger.
 */
const PaloFanSection: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isMobile = useMedia("(max-width: 900px)");
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  const header = (
    <EraHeader
      era={PALO_SECTION}
      statementInk="Five products, five industries, one consultancy."
      statementMuted="Government booking systems, roadside rescue, retail banking, blockchain and heavy-load trucking."
    />
  );

  if (isMobile || reduced) {
    return (
      <Wrap data-era={1}>
        <Inner>
          {header}
          <MobileColumn>
            {PALO_SECTION.cards.map((card, i) => (
              <Reveal key={card.project.id} delay={Math.min(i * 0.08, 0.24)}>
                <TradingCard card={card} media={cardMedia(card)} />
              </Reveal>
            ))}
          </MobileColumn>
        </Inner>
      </Wrap>
    );
  }

  return (
    <Wrap data-era={1}>
      <Inner>{header}</Inner>
      <FanTrack ref={ref}>
        <FanViewport>
          <Inner style={{ width: "100%" }}>
            <FanStage>
              {PALO_SECTION.cards.map((card, i) => (
                <FanCard
                  key={card.project.id}
                  card={card}
                  index={i}
                  progress={scrollYProgress}
                />
              ))}
            </FanStage>
          </Inner>
        </FanViewport>
      </FanTrack>
    </Wrap>
  );
};

export default PaloFanSection;
